<template>
    <div class="max-w-(--breakpoint-sm) flex flex-col gap-4">
        <page-header>Plan Information</page-header>

        <!-- Upgrade Alert -->
        <vx-alert v-if="props.teamPlan?.attributes?.recharge" :closeable="false">
            To <a href="mailto:support@voxie.com?subject=Upgrade My Plan">upgrade your plan</a>
            or for other billing related questions, please contact
            <a href="mailto:support@voxie.com">support@voxie.com</a>.
        </vx-alert>

        <!-- Plan not loaded alert -->
        <vx-alert v-if="!props.teamPlan" color="info" :closeable="false" data-test="no-plan-alert">
            Your plan information could not be loaded. Please contact
            <a href="mailto:support@voxie.com">support@voxie.com</a>.
        </vx-alert>

        <!-- Plan Details Card -->
        <vx-expandable-card v-if="props.teamPlan" data-test="plan-details-card">
            <template v-slot:header>{{ props.teamPlan.name }}</template>
            <template v-if="upcomingInvoice" v-slot:headerRight>
                <span>
                    {{ currencyCents(upcomingInvoice.total) }}
                    <span class="text-sm text-slate-400"> /{{ props.teamPlan.is_annual ? 'year' : 'month' }} </span>
                </span>
            </template>
            <template v-slot:content>
                <div class="flex flex-col gap-4 mt-3">
                    <div class="whitespace-pre-wrap">{{ planDescription }}</div>

                    <div v-if="loading" class="py-3 text-center">
                        <vx-icon-loading class="w-9 h-9" spin />
                    </div>

                    <vx-alert v-else-if="error" :closeable="false" color="danger" data-test="usage-error-alert">
                        Failed to load usage for this month.
                    </vx-alert>

                    <template v-else>
                        <!-- Recharge plan info -->
                        <div
                            v-if="props.teamPlan.attributes?.recharge"
                            data-test="recharge-info"
                            class="flex items-center justify-between"
                        >
                            <div>
                                <div
                                    v-if="
                                        props.teamMessageBalance !== undefined &&
                                        !props.teamPlan.attributes?.no_rollover
                                    "
                                >
                                    Current balance: {{ formatNumber(props.teamMessageBalance) }} segments
                                </div>
                                <div v-else-if="props.teamPlan.attributes?.no_rollover">
                                    Current balance: {{ formatNumber(currentBalanceForNoRollover) }} segments
                                </div>
                                <div>Usage this month: {{ formatNumber(totalCreditsUsed) }} segments</div>
                                <div v-if="props.teamPlan.attributes?.no_rollover">
                                    Number of recharges: {{ formatNumber(numberOfRecharges) }}
                                </div>
                            </div>
                            <vx-button
                                v-if="isImpersonatorOrPlatformAdmin && hasMessageBalance"
                                color="muted"
                                data-test="adjust-balance-button"
                                @click="isMessageBalanceModalVisible = true"
                            >
                                Adjust
                            </vx-button>
                        </div>
                        <modal-adjust-message-balance
                            v-if="isImpersonatorOrPlatformAdmin && hasMessageBalance"
                            v-model="messageBalance"
                            v-model:visible="isMessageBalanceModalVisible"
                            :teamId="props.team.id"
                        />

                        <!-- Usage-based plan info -->
                        <div v-if="!props.teamPlan.attributes?.recharge" data-test="usage-info">
                            SMS usage this month: {{ formatNumber(usageByType.sms) }} segments <br />
                            MMS usage this month: {{ formatNumber(usageByType.mms) }} messages
                        </div>

                        <vx-alert color="info" :closeable="false">
                            These estimates can take a few hours to update and are not necessarily reflective of final
                            invoice amounts.
                        </vx-alert>
                    </template>
                </div>
            </template>
        </vx-expandable-card>

        <!-- Estimated Invoice Card -->
        <vx-expandable-card v-if="upcomingInvoice" class="w-full" data-test="estimated-invoice-card">
            <template v-slot:header>Estimated Invoice</template>
            <template v-slot:content>
                <div class="flex flex-col gap-4 mt-3">
                    <div>
                        This is an estimate based on usage so far this month. These numbers can take a few hours to
                        update and are not necessarily reflective of final invoice amounts.
                    </div>
                    <invoice-preview :invoice="upcomingInvoice" />
                </div>
            </template>
        </vx-expandable-card>
    </div>
</template>

<script setup lang="ts">
import { VxAlert, VxButton, VxExpandableCard, VxIconLoading } from '@voxie/frontend-components';
import { computed, onMounted, ref } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { analyticsClient, teamsClient } from '~/services';
import { currencyCents, formatNumber } from '~/utils/helpers';
import { getCurrentMonthDateRange } from '~/composables/useBillingDates';
import InvoicePreview from '~/components/settings/teams/plan-information/InvoicePreview.vue';
import ModalAdjustMessageBalance from '~/components/settings/subscription/ModalAdjustMessageBalance.vue';

interface TeamPlanAttributes {
    recharge?: boolean;
    no_rollover?: boolean;
    messages?: number;
    recharge_cost?: number;
    recharge_messages?: number;
}

interface TeamPlan {
    name: string;
    is_annual?: boolean;
    attributes?: TeamPlanAttributes;
}

interface Team {
    id: number;
    status: string;
}

interface UsageByTypeRecord {
    usage_type: string;
    values: number[];
}

interface Props {
    team: Team;
    teamPlan?: TeamPlan | null;
    teamMessageBalance?: number;
    teamSumBalanceAmount?: number;
    isAdminOrOwner?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    teamPlan: null,
    teamMessageBalance: 0,
    teamSumBalanceAmount: 0,
    isAdminOrOwner: false,
});

const isImpersonatorOrPlatformAdmin = window.Spark?.isImpersonating || window.Spark?.isPlatformAdmin;

interface InvoiceLineItem {
    id?: string;
    description?: string;
    amount?: number;
}

interface UpcomingInvoice {
    total: number;
    lines?: { data: InvoiceLineItem[] };
    period_end?: number;
}

const error = ref(false);
const loading = ref(true);
const usageByTypeRaw = ref<UsageByTypeRecord[]>([]);
const upcomingInvoice = ref<UpcomingInvoice | null>(null);
const messageBalance = ref(String(props.teamMessageBalance));
const isMessageBalanceModalVisible = ref(false);

const usageByType = computed(() => {
    let mms = 0;
    let sms = 0;

    usageByTypeRaw.value.forEach((record) => {
        switch (record.usage_type) {
            case 'mms_message':
                mms += record.values[0] ?? 0;
                break;
            case 'sms_segment':
                sms += record.values[0] ?? 0;
                break;
        }
    });

    return { mms, sms };
});

const planDescription = computed(() => {
    if (!props.teamPlan) {
        return '';
    }

    const hasRecharge = props.teamPlan.attributes?.recharge;
    const hasRollover = !props.teamPlan.attributes?.no_rollover;
    const isUsageBased = !hasRecharge;

    if (hasRecharge && hasRollover) {
        return `This plan includes ${formatNumber(
            props.teamPlan.attributes?.messages ?? 0
        )} segment credits with segment balance rollover. Unused segment credits will transfer to the next period and the rollover balance resets at the beginning of the calendar year.`;
    }

    if (hasRecharge && !hasRollover) {
        const rechargeCost = Math.ceil((props.teamPlan.attributes?.recharge_cost ?? 0) / 100);
        return `This plan includes ${formatNumber(
            props.teamPlan.attributes?.messages ?? 0
        )} segment credits.\nIf the balance is completely used during a month, your card on file will immediately be charged $${formatNumber(
            rechargeCost
        )} and ${formatNumber(
            props.teamPlan.attributes?.recharge_messages ?? 0
        )} segment credits will be added to your balance.`;
    }

    if (isUsageBased) {
        return `You are on a custom usage-based plan. Please see your individual contract for billing details and rates.`;
    }

    return '';
});

const planMessages = computed(() => props.teamPlan?.attributes?.messages ?? 0);

const totalCreditsUsed = computed(() => usageByType.value.mms * 2 + usageByType.value.sms);

const numberOfRecharges = computed(() => {
    if (!props.teamPlan?.attributes?.recharge) {
        return 0;
    }
    const recharges = Math.ceil((totalCreditsUsed.value - planMessages.value) / (props.teamSumBalanceAmount || 1));
    return recharges < 0 ? 0 : recharges;
});

const currentBalanceForNoRollover = computed(() => planMessages.value + (props.teamSumBalanceAmount ?? 0));

const hasMessageBalance = computed(() => {
    return (props.teamPlan?.attributes?.recharge ?? false) && !(props.teamPlan?.attributes?.no_rollover ?? false);
});

const fetchUsageTotalByType = async () => {
    loading.value = true;

    const dateRange = getCurrentMonthDateRange();

    // Early-month guard: if less than 1 hour of data, skip fetch and show zero usage
    if (dateRange === null) {
        usageByTypeRaw.value = [];
        loading.value = false;
        return;
    }

    try {
        const response = await analyticsClient.getUsageTotalByType(
            { teamId: props.team.id },
            {
                range: [dateRange.start_date, dateRange.end_date],
                filters: { usage_types: ['mms_message', 'sms_segment'] },
            }
        );
        usageByTypeRaw.value = response.data?.data ?? [];
    } catch (e) {
        console.error(e);
        usageByTypeRaw.value = [];
        error.value = true;
    } finally {
        loading.value = false;
    }
};

const fetchUpcomingInvoice = async () => {
    try {
        const response = await teamsClient.fetchUpcomingInvoice(props.team.id);
        upcomingInvoice.value = response?.data?.invoice ?? null;
    } catch (e) {
        console.error(e);
    }
};

onMounted(() => {
    fetchUsageTotalByType();
    fetchUpcomingInvoice();
});
</script>
